import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DialogContainer from '@shared/ui/dialog/DialogContainer';
import { container } from '@infrastructure/container';
import { clearAmazonOAuthIntent, readAmazonOAuthIntent } from '../amazonOAuth';

/** Amazon redirects here with `spapi_oauth_code` after the seller approves the app. */
const AmazonCallback = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const started = useRef(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const returnPath = readAmazonOAuthIntent()?.returnPath || '/';

    useEffect(() => {
        const oauthError = searchParams.get('error_description') || searchParams.get('error');
        const code = searchParams.get('spapi_oauth_code');
        const state = searchParams.get('state');
        const sellingPartnerId = searchParams.get('selling_partner_id') ?? undefined;

        if (oauthError) {
            setError(oauthError);
            setIsLoading(false);
            return;
        }

        if (!code || !state) {
            setError('Amazon did not return an authorization code. Start the connection again.');
            setIsLoading(false);
            return;
        }

        if (started.current) return;
        started.current = true;

        container.amazonConnectorRepository.completeAuthorization(code, state, sellingPartnerId)
            .then((response) => {
                if (response.status === 'error') {
                    setError(response.error.message ?? 'Failed to connect to Amazon');
                    return;
                }
                clearAmazonOAuthIntent();
                if (!response.data.active) {
                    navigate(`/company/connections/${response.data.id}/setup`);
                } else {
                    navigate(returnPath);
                }
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Failed to connect to Amazon');
            })
            .finally(() => setIsLoading(false));
    }, [searchParams, navigate, returnPath]);

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
            <DialogContainer maxWidth="600px">
                <Box sx={{ position: 'relative', margin: 4 }}>
                    <img src="/assets/images/ic_just_stock.png" alt="JustStock" style={{ height: 16 }} />
                    {isLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {error && (
                        <Box sx={{ pt: 3 }}>
                            <Typography color="error.main">{error}</Typography>
                            <Stack direction="row" sx={{ mt: 2 }}>
                                <Button variant="outlined" onClick={() => navigate(returnPath)}>Back</Button>
                            </Stack>
                        </Box>
                    )}
                </Box>
            </DialogContainer>
        </Box>
    );
};

export default AmazonCallback;
